import type { MyFeedScope } from '@pulse/shared';
import {
  Box,
  Button,
  Chip,
  Divider,
  ICON_SIZE,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  type Theme,
  Typography,
  useIsDesktop,
  useTheme,
} from '@pulse/ui';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FilterPill } from '../../../components/FilterPill';
import { SearchField } from '../../../components/SearchField';
import { HOME_TABS, type HomeTabConfig, type HomeTabKind, type HomeWorkRow } from '../types';

import { WorkItemCard } from './WorkItemCard';
import { WorkItemRow } from './WorkItemRow';

type GroupBy = 'none' | 'project' | 'due' | 'status';

const GROUP_BY_LABELS: Record<GroupBy, string> = {
  none: 'None',
  project: 'Project',
  due: 'Due',
  status: 'Status',
};

interface FilterChip {
  key: string;
  label: string;
  matches: (row: HomeWorkRow) => boolean;
}

/** Non-empty by construction — every mode leads with an "All" chip, so `[0]` is always a real
 *  fallback when the active filter key no longer exists. */
type FilterChips = [FilterChip, ...FilterChip[]];

function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

// Quick-filter chips — one shared set for every tab. Status filtering deliberately lives in the
// Status FilterPill instead of here: an entity with ten workflow steps (risk) wrapped the chip
// row onto two lines, and a dropdown holds any number of them in constant space.
//
// No chip matches on a status name. "Is this finished?" is answered by workflow_steps.isTerminal
// via `terminalStatuses`, so renaming a step in admin config can't silently break Overdue.
//
// Expedited/Flagged are issue-only boolean columns (not statuses, so nothing to de-hardcode);
// `supportsIssueFlags` is the only thing that now differs between tabs.
function buildFilterChips(
  config: HomeTabConfig,
  terminalStatuses: ReadonlySet<string>,
  today: string,
): FilterChips {
  const isOverdue = (r: HomeWorkRow) =>
    Boolean(r.dueDate && r.dueDate < today && !terminalStatuses.has(r.status));

  // Ordered by urgency — Overdue before Due Today before the flags — so the chips read left to
  // right as "needs attention now" → "needs attention today" → "marked important".
  return [
    { key: 'all', label: `All ${config.tabLabel}`, matches: () => true },
    { key: 'overdue', label: 'Overdue', matches: isOverdue },
    { key: 'dueToday', label: 'Due Today', matches: (r) => r.dueDate === today },
    // "Blocked" in the reference design → our Issue.flagged field (no Blocked workflow step
    // exists in this product — see the plan's Decision #1).
    ...(config.supportsIssueFlags
      ? [
          { key: 'expedited', label: 'Expedite', matches: (r: HomeWorkRow) => Boolean(r.issue?.expedited) },
          { key: 'flagged', label: 'Flagged', matches: (r: HomeWorkRow) => Boolean(r.issue?.flagged) },
        ]
      : []),
  ];
}

// Stable sort (Array#sort is stable in every JS engine this app targets) — expedited items
// float to the top of their group/tab while everything else keeps its original relative order.
// `?? false` matters: a kind with no `issue` block would otherwise produce NaN comparisons.
function expeditedFirst(rows: HomeWorkRow[]): HomeWorkRow[] {
  return [...rows].sort((a, b) => Number(b.issue?.expedited ?? false) - Number(a.issue?.expedited ?? false));
}

function dueBucket(row: HomeWorkRow, isDone: (row: HomeWorkRow) => boolean, today: string, tomorrow: string): string {
  if (isDone(row)) return 'Completed';
  if (!row.dueDate) return 'No Due Date';
  if (row.dueDate < today) return 'Overdue';
  if (row.dueDate === today) return 'Today';
  if (row.dueDate === tomorrow) return 'Tomorrow';
  return 'Upcoming';
}

function groupDotColor(theme: Theme, groupBy: GroupBy, key: string): string {
  if (groupBy === 'due') {
    if (key === 'Overdue') return theme.palette.error.main;
    if (key === 'Today') return theme.palette.warning.main;
    if (key === 'Completed') return theme.palette.success.main;
    return theme.palette.text.disabled;
  }
  if (groupBy === 'status') {
    const s = key.toLowerCase();
    if (s.includes('done')) return theme.palette.success.main;
    if (s.includes('review')) return '#423699';
    if (s.includes('progress')) return theme.palette.secondary.dark;
    return theme.palette.text.disabled;
  }
  return theme.palette.secondary.main;
}

// The grouped list, flattened into one sequence of group headers and rows so it can be
// virtualized. `gapAfter` (theme spacing units) reproduces the spacing the nested Stacks used to
// give: 1 after a header and between rows, 2 between groups, none after the last item.
type ListItem =
  | { type: 'header'; key: string; groupKey: string; label: string; count: number; gapAfter: number }
  | { type: 'row'; key: string; row: HomeWorkRow; gapAfter: number };

// A Project Lead's view can hold thousands of rows (every item on every project they run), and
// mounting them all froze the page. Only the rows near the viewport are mounted; OVERSCAN_ITEMS
// more on each side keep ordinary scrolling from showing blank space. Real heights are measured
// once mounted, so these estimates only need to be close.
const OVERSCAN_ITEMS = 8;
const ESTIMATED_HEADER_HEIGHT = 32;
const ESTIMATED_ROW_HEIGHT = 72;
const ESTIMATED_CARD_HEIGHT = 160;

interface MyWorkPanelProps {
  config: HomeTabConfig;
  /** Lead/Manager/BU Head view lists the whole team's work, not just the caller's own — the
   *  panel title reflects that ("All Tasks" vs "My Tasks") so it doesn't misleadingly claim
   *  every row belongs to the viewer. */
  scope: MyFeedScope;
  onTabChange: (kind: HomeTabKind) => void;
  rows: HomeWorkRow[];
  /** Owned by HomePage rather than this component so it survives the per-tab remount — a search
   *  term stays applied when you switch tabs, unlike the chip/status/grouping selections, which
   *  are kind-specific and deliberately reset. */
  search: string;
  onSearchChange: (value: string) => void;
  /** Workflow step names for this kind, in `order`. Drives the row's StatusPill dropdown, the
   *  Status filter, and Group By → Status. */
  statusOptions: string[];
  /** Step names flagged `isTerminal` — the DB-driven stand-in for "is this finished", used by
   *  the Overdue chip and the Due grouping so no status name has to be hardcoded. */
  terminalStatuses: string[];
  onToggleExpedited: (row: HomeWorkRow) => void;
  /** Whether this row's Expedite bolt is actionable — see WorkItemRow. Passed straight through. */
  canExpedite: (row: HomeWorkRow) => boolean;
  onStatusChange: (row: HomeWorkRow, toStep: string) => void;
  onLogTime: (row: HomeWorkRow) => void;
  onOpenComments: (row: HomeWorkRow) => void;
  /** Opens the kind's own form dialog. Only rows on a tab whose config sets a
   *  `formDialogAction` render the button that calls this. */
  onOpenFormDialog: (row: HomeWorkRow) => void;
}

export function MyWorkPanel({
  config,
  scope,
  onTabChange,
  rows,
  search,
  onSearchChange,
  statusOptions,
  terminalStatuses,
  onToggleExpedited,
  canExpedite,
  onStatusChange,
  onLogTime,
  onOpenComments,
  onOpenFormDialog,
}: MyWorkPanelProps) {
  const theme = useTheme();
  const isCardLayout = !useIsDesktop();
  const [activeFilter, setActiveFilter] = useState('all');
  // Starts empty and is replaced with the non-terminal steps as soon as the workflow resolves
  // (see the effect below) — it can't be a useState initialiser because statusOptions arrives
  // from a fetch.
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  // Grouped by due date on first load — an undated backlog reads better bucketed into
  // Overdue/Today/Tomorrow than as one flat list. Applies to every tab: the panel is remounted
  // per kind (see HomePage's `key`), so each tab starts here rather than inheriting the last
  // tab's choice.
  const [groupBy, setGroupBy] = useState<GroupBy>('due');

  const today = todayIso();
  const tomorrow = new Date(Date.now() + 86_400_000).toLocaleDateString('en-CA');

  const terminalStatusSet = useMemo(() => new Set(terminalStatuses), [terminalStatuses]);

  // Default the Status filter to every non-terminal step, so Home opens on work still in
  // flight rather than a backlog padded with finished items — same default ProjectRisksTab and
  // the other list screens apply. Guarded by a ref so it runs once per tab and never clobbers a
  // selection the user has since made (the workflow query refetching in the background would
  // otherwise reset their choice). The panel remounts per tab, so each one gets its own default.
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (statusOptions.length === 0 || defaultsAppliedRef.current) return;
    setSelectedStatuses(statusOptions.filter((name) => !terminalStatusSet.has(name)));
    defaultsAppliedRef.current = true;
  }, [statusOptions, terminalStatusSet]);
  // "Is this item finished?" — answered by workflow_steps.isTerminal for every tab, so no status
  // name appears here. Verified equivalent to the previous `status === 'Done'` for issues: Done
  // is the only terminal step in the issue workflow.
  const isDone = useMemo(
    () => (row: HomeWorkRow) => terminalStatusSet.has(row.status),
    [terminalStatusSet],
  );

  const filterChips = useMemo(
    () => buildFilterChips(config, terminalStatusSet, today),
    [config, terminalStatusSet, today],
  );

  // Lead/Manager/BU Head view lists the whole team's work on the caller's projects, not just
  // their own — "My Tasks" would be misleading there, so it reads "All Tasks" instead. Individual
  // view keeps the original per-kind title from config.
  const panelTitle = scope === 'led' ? `All ${config.tabLabel}` : config.panelTitle;
  // Same reasoning as panelTitle above — "assigned to you"/"you own" phrasing doesn't hold once
  // the list is the whole team's work, so the led view gets its own subtitle per kind.
  const panelSubtitle = scope === 'led' ? config.panelSubtitleLed : config.panelSubtitle;

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.title.toLowerCase().includes(q) || r.projectName.toLowerCase().includes(q));
  }, [rows, search]);

  // Status (multi-select) and the active quick-filter chip are ANDed, same as ProjectRisksTab
  // where search + status + quick filters all narrow together. Applied before the chip so the
  // chip counts below stay honest: each one reports what you'd actually get by clicking it,
  // given the search and status already in effect.
  const statusFiltered = useMemo(
    () => (selectedStatuses.length === 0 ? searched : searched.filter((r) => selectedStatuses.includes(r.status))),
    [searched, selectedStatuses],
  );

  const chipCounts = useMemo(
    () => Object.fromEntries(filterChips.map((c) => [c.key, statusFiltered.filter(c.matches).length])) as Record<string, number>,
    [statusFiltered, filterChips],
  );

  // Guards against an active chip key that no longer exists (e.g. Expedited while switching to a
  // tab without issue flags) — fall back to "all" rather than showing an empty list.
  // "All" is the unfiltered state, so an active chip only counts as a filter when it isn't that.
  const hasFilters = Boolean(search.trim()) || selectedStatuses.length > 0 || activeFilter !== 'all';

  function clearFilters() {
    onSearchChange('');
    setSelectedStatuses([]);
    setActiveFilter('all');
  }

  const activeChip: FilterChip = filterChips.find((c) => c.key === activeFilter) ?? filterChips[0];
  const tabFiltered = useMemo(() => statusFiltered.filter(activeChip.matches), [statusFiltered, activeChip]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: null as string | null, rows: expeditedFirst(tabFiltered) }];

    if (groupBy === 'project') {
      const map = new Map<string, HomeWorkRow[]>();
      const projectLabel = new Map<string, string>();
      for (const r of tabFiltered) {
        const list = map.get(r.projectId) ?? [];
        list.push(r);
        map.set(r.projectId, list);
        projectLabel.set(r.projectId, `${r.projectKey} — ${r.projectName}`);
      }
      return [...map.entries()]
        .map(([key, groupRows]) => ({ key, label: projectLabel.get(key) ?? key, rows: expeditedFirst(groupRows) }))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
    }

    if (groupBy === 'status') {
      const map = new Map<string, HomeWorkRow[]>();
      for (const r of tabFiltered) {
        const list = map.get(r.status) ?? [];
        list.push(r);
        map.set(r.status, list);
      }
      return statusOptions.filter((s) => map.has(s)).map((s) => ({ key: s, label: s, rows: expeditedFirst(map.get(s)!) }));
    }

    // due
    const bucketOrder = ['Overdue', 'Today', 'Tomorrow', 'Upcoming', 'No Due Date', 'Completed'];
    const map = new Map<string, HomeWorkRow[]>();
    for (const r of tabFiltered) {
      const b = dueBucket(r, isDone, today, tomorrow);
      const list = map.get(b) ?? [];
      list.push(r);
      map.set(b, list);
    }
    return bucketOrder.filter((b) => map.has(b)).map((b) => ({ key: b, label: b, rows: expeditedFirst(map.get(b)!) }));
  }, [tabFiltered, groupBy, statusOptions, isDone, today, tomorrow]);

  const listItems = useMemo(() => {
    const items: ListItem[] = [];
    groups.forEach((group, groupIndex) => {
      const isLastGroup = groupIndex === groups.length - 1;
      if (group.label !== null) {
        items.push({ type: 'header', key: `group:${group.key}`, groupKey: group.key, label: group.label, count: group.rows.length, gapAfter: 1 });
      }
      group.rows.forEach((row, rowIndex) => {
        const isLastRow = rowIndex === group.rows.length - 1;
        items.push({ type: 'row', key: row.id, row, gapAfter: !isLastRow ? 1 : isLastGroup ? 0 : 2 });
      });
    });
    return items;
  }, [groups]);

  // The virtualizer measures against the window's scroll position, so it needs the distance from
  // the top of the page to where the list starts (scrollMargin). Anything above the list changing
  // height (filter chips wrapping, the KPI row, window resize) moves it, so it's re-measured
  // whenever the page's size changes rather than hardcoded — same approach as DailyLogTab.
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => setScrollMargin(list.getBoundingClientRect().top + window.scrollY);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: listItems.length,
    estimateSize: (index) =>
      listItems[index]?.type === 'header' ? ESTIMATED_HEADER_HEIGHT : isCardLayout ? ESTIMATED_CARD_HEIGHT : ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => listItems[index]?.key ?? index,
    overscan: OVERSCAN_ITEMS,
    scrollMargin,
  });

  return (
    <Paper sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{panelTitle}</Typography>
            <Typography variant="caption" color="text.secondary">{panelSubtitle}</Typography>
          </Box>
          <SearchField
            placeholder={config.searchPlaceholder}
            value={search}
            onChange={onSearchChange}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={config.kind}
            onChange={(_e, kind: HomeTabKind) => onTabChange(kind)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {HOME_TABS.map((t) => (
              <Tab
                key={t.kind}
                label={t.tabLabel}
                value={t.kind}
                icon={<t.icon sx={{ fontSize: ICON_SIZE.xxs }} />}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap gap={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {filterChips.map((c) => {
              const isActive = activeChip.key === c.key;
              const toggle = () => setActiveFilter(isActive && c.key !== 'all' ? 'all' : c.key);
              return (
                <Chip
                  key={c.key}
                  label={`${c.label} (${chipCounts[c.key]})`}
                  size="small"
                  onClick={toggle}
                  onDelete={c.key !== 'all' && isActive ? toggle : undefined}
                  color={isActive ? 'primary' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  sx={{ height: 30, borderRadius: '15px', fontWeight: isActive ? 600 : 400 }}
                />
              );
            })}
            {/* Every status for this kind, terminal ones included — unlike the chip row this has
                constant width, so there's no reason to hide any of them. Same shared component
                ProjectRisksTab uses for the identical job. */}
            <FilterPill
              label="Status"
              options={statusOptions}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
            />
            {/* Same affordance as ProjectRisksTab and the other list pages — appears only once
                something is actually filtering, and resets search, status and the active chip
                together. Group By is untouched: it rearranges rows, it doesn't hide any. */}
            {hasFilters && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Button
                  variant="text"
                  size="small"
                  onClick={clearFilters}
                  sx={{ color: 'text.secondary', fontWeight: 400 }}
                >
                  Clear filters
                </Button>
              </>
            )}
          </Stack>
          {/* Sized to the 32px filter row rather than MUI's 40px `size="small"` default, and
              labelled inline ("Group By: Due") instead of with a floating InputLabel, so it
              reads as one of the filters rather than a form field. */}
          <Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            size="small"
            renderValue={(v) => `Group By: ${GROUP_BY_LABELS[v as GroupBy]}`}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'auto' },
              height: 32,
              borderRadius: '16px',
              '& .MuiSelect-select': { py: 0, pl: 1.5, fontSize: '0.875rem', display: 'flex', alignItems: 'center' },
            }}
          >
            {(Object.keys(GROUP_BY_LABELS) as GroupBy[]).map((key) => (
              <MenuItem key={key} value={key}>{GROUP_BY_LABELS[key]}</MenuItem>
            ))}
          </Select>
        </Stack>

        {/* The list container always renders (0px tall when empty) so listRef — and the
            scrollMargin measured from it — exists from the first render. */}
        <Box>
          {listItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {config.noMatchesText}
            </Typography>
          )}
          <Box ref={listRef} sx={{ position: 'relative' }} style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const item = listItems[virtualItem.index];
              if (!item) return null;
              return (
                <Box
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  sx={{ position: 'absolute', left: 0, right: 0, pb: item.gapAfter }}
                  style={{ top: virtualItem.start - scrollMargin }}
                >
                  {item.type === 'header' ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: groupDotColor(theme, groupBy, item.groupKey) }} />
                      <Typography variant="body2" fontWeight={700}>{item.label}</Typography>
                      <Typography variant="caption" color="text.secondary">({item.count})</Typography>
                    </Stack>
                  ) : isCardLayout ? (
                    <WorkItemCard
                      row={item.row}
                      statusOptions={statusOptions}
                      onToggleExpedited={onToggleExpedited}
                      canExpedite={canExpedite}
                      onStatusChange={onStatusChange}
                      onLogTime={onLogTime}
                      onOpenComments={onOpenComments}
                      formDialogAction={config.formDialogAction}
                      onOpenFormDialog={onOpenFormDialog}
                    />
                  ) : (
                    <WorkItemRow
                      row={item.row}
                      statusOptions={statusOptions}
                      onToggleExpedited={onToggleExpedited}
                      canExpedite={canExpedite}
                      onStatusChange={onStatusChange}
                      onLogTime={onLogTime}
                      onOpenComments={onOpenComments}
                      formDialogAction={config.formDialogAction}
                      onOpenFormDialog={onOpenFormDialog}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}
